/**
 * Hook React pour l'authentification
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Ce hook encapsule toute la logique d'authentification :
 * - Connexion/Déconnexion
 * - Gestion du profil utilisateur
 * - Vérification 2FA
 * - Rafraîchissement des tokens
 * - Gestion des erreurs
 */

  // Client Component pour Next.js

import { useAuthStore, type AuthState } from '../store/authStore';
import type {
  LoginCredentials,
  TwoFactorVerification,
  UpdateProfileData,
  ChangePasswordData,
  ChangeEmailData,
  VerifyEmailChangeData,
  UserProfile,
} from '../../application/dto/dtos';

/**
 * Interface du hook useAuth
 */
export interface UseAuthReturn {
  // État
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requires2FA: boolean;
  requires2FAConfiguration: boolean;  // ← Indique si 2FA doit être configuré
  twoFactorSessionData: {
    idUtilisateur?: string;
    sessionId?: string;
    methode2FA?: 'EMAIL' | 'APPLICATION';
  } | null;
  error: string | null;
  isHydrated: boolean;  // ← Indique si la hydratation Zustand est complète

  // Actions - Authentification
  login: (credentials: LoginCredentials) => Promise<void>;
  verify2FA: (verification: TwoFactorVerification) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;

  // Actions - Profil
  getProfile: () => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  changeEmail: (data: ChangeEmailData) => Promise<void>;
  verifyEmailChange: (data: VerifyEmailChangeData) => Promise<void>;

  // Actions - 2FA
  activate2FA: (methode: 'EMAIL' | 'APPLICATION', code: string, secret?: string) => Promise<string[]>;
  deactivate2FA: (motDePasse: string, code: string) => Promise<void>;
  verifyBackupCode: (data: { idUtilisateur: string; code: string }) => Promise<boolean>;

  // Utilitaires
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook principal d'authentification
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const { login, isLoading, error, clearError } = useAuth();
 *
 *   const handleLogin = async (email: string, password: string) => {
 *     try {
 *       await login({ email, motDePasse: password });
 *     } catch (err) {
 *       // Erreur déjà gérée dans le store
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleLogin}>
 *       {error && <div>{error}</div>}
 *       // ... autres champs du formulaire
 *     </form>
 *   );
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const store = useAuthStore();
  // isHydrated est global au store : positionne une seule fois par l'appel
  // racine de bootstrapAuth() (voir App.tsx), jamais re-declenche ici — ce
  // hook est consomme par ~30 composants et ne doit avoir aucun effet de bord.
  const isHydrated = store.isHydrated;

  return {
    // État
    user: store.user,
    isAuthenticated: isHydrated && store.isAuthenticated,
    isLoading: store.isLoading,
    requires2FA: isHydrated ? store.requires2FA : false,
    requires2FAConfiguration: isHydrated ? store.requires2FAConfiguration : false,
    twoFactorSessionData: isHydrated ? store.twoFactorSessionData : null,
    error: store.error,
    isHydrated,  // ← Exposer l'état de hydratation

    // Actions - Authentification
    login: store.login,
    verify2FA: store.verify2FA,
    logout: store.logout,
    refreshToken: store.refreshToken,

    // Actions - Profil
    getProfile: store.getProfile,
    updateProfile: store.updateProfile,
    changePassword: store.changePassword,
    changeEmail: store.changeEmail,
    verifyEmailChange: store.verifyEmailChange,

    // Actions - 2FA
    activate2FA: store.activate2FA,
    deactivate2FA: store.deactivate2FA,
    verifyBackupCode: store.verifyBackupCode,

    // Utilitaires
    setError: store.setError,
    clearError: store.clearError,
    reset: store.reset,
  };
}

/**
 * Hook pour obtenir uniquement l'utilisateur connecté
 * Version optimisée qui ne déclenche pas de re-render inutiles
 *
 * @example
 * ```tsx
 * function UserMenu() {
 *   const user = useCurrentUser();
 *
 *   if (!user) return <LoginButton />;
 *
 *   return <div>Bonjour {user.nom}</div>;
 * }
 * ```
 */
export function useCurrentUser(): UserProfile | null {
  return useAuthStore((state: AuthState) => state.user);
}

/**
 * Hook pour obtenir uniquement le statut d'authentification
 * Version optimisée qui ne déclenche pas de re-render inutiles
 *
 * @example
 * ```tsx
 * function ProtectedContent() {
 *   const isAuthenticated = useIsAuthenticated();
 *
 *   if (!isAuthenticated) return <Redirect to="/login" />;
 *
 *   return <div>Contenu protégé</div>;
 * }
 * ```
 */
export function useIsAuthenticated(): boolean {
  return useAuthStore((state: AuthState) => state.isAuthenticated);
}

/**
 * Hook pour obtenir uniquement l'état de chargement
 * Utile pour afficher des loaders globaux
 *
 * @example
 * ```tsx
 * function App() {
 *   const isLoading = useAuthLoading();
 *
 *   return (
 *     <>
 *       {isLoading && <GlobalLoader />}
 *       <Routes />
 *     </>
 *   );
 * }
 * ```
 */
export function useAuthLoading(): boolean {
  return useAuthStore((state: AuthState) => state.isLoading);
}

/**
 * Hook pour obtenir uniquement les erreurs d'authentification
 * Utile pour afficher des messages d'erreur globaux
 *
 * @example
 * ```tsx
 * function GlobalErrorDisplay() {
 *   const error = useAuthError();
 *   const clearError = useAuthStore((state) => state.clearError);
 *
 *   if (!error) return null;
 *
 *   return (
 *     <Alert onClose={clearError}>
 *       {error}
 *     </Alert>
 *   );
 * }
 * ```
 */
export function useAuthError(): string | null {
  return useAuthStore((state: AuthState) => state.error);
}
